import React, { useEffect, useState } from 'react';
import { Outlet, NavLink, Link, useNavigate, useLocation } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { logout } from '../../store/slices/authSlice';
import { fetchAvailability, setMyStatus, setMine } from '../../store/slices/availabilitySlice';
import { fetchRolePermissions } from '../../store/slices/permissionsSlice';
import client from '../../api/client';

const STATUS_STYLE = {
  ONLINE:  { dot: 'bg-emerald-400', text: 'text-emerald-300', label: 'Online' },
  BUSY:    { dot: 'bg-amber-400',   text: 'text-amber-300',   label: 'Busy' },
  OFFLINE: { dot: 'bg-gray-500',    text: 'text-gray-400',    label: 'Offline' },
};
const CYCLE = { ONLINE: 'BUSY', BUSY: 'OFFLINE', OFFLINE: 'ONLINE' };

function StatusPill({ status, onToggle }) {
  const s = STATUS_STYLE[status] || STATUS_STYLE.OFFLINE;
  return (
    <button onClick={onToggle} title="Click to change status"
      className="flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-white/10 hover:bg-white/20 transition-colors">
      <span className={`w-2 h-2 rounded-full ${s.dot}`} />
      <span className={`text-xs font-semibold ${s.text}`}>{s.label}</span>
    </button>
  );
}

const mainNavItems = [
  { to: '/tickets', label: 'Tickets', permission: 'view_own_tickets', icon: (
    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 5v2m0 4v2m0 4v2M5 5a2 2 0 00-2 2v3a2 2 0 110 4v3a2 2 0 002 2h14a2 2 0 002-2v-3a2 2 0 110-4V7a2 2 0 00-2-2H5z" /></svg>
  )},
  { to: '/rma-tickets', label: 'RMA Tickets', icon: (
    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7h12m0 0l-4-4m4 4l-4 4m0 6H4m0 0l4 4m-4-4l4-4" /></svg>
  )},
  { to: '/pm-tickets', label: 'PM Tickets', icon: (
    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-6 9l2 2 4-4" /></svg>
  )},
  { to: '/am-tickets', label: 'Asset Management', icon: (
    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 17v-2m3 2v-4m3 4v-6m2 10H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"/></svg>
  )},
  { to: '/inventory', label: 'Device Inventory', icon: (
    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9.75 17L9 20l-1 1h8l-1-1-.75-3M3 13h18M5 17h14a2 2 0 002-2V5a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" /></svg>
  )},
  { to: '/spares', label: 'Spares', icon: (
    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" /><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" /></svg>
  )},
  { to: '/agents', label: 'Site Engineers', permission: 'view_agents', icon: (
    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0z" /></svg>
  )},
  { to: '/documents', label: 'Documents', icon: (
    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" /></svg>
  )},
  { to: '/tl-dashboard', label: 'TL Dashboard', icon: (
    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" /></svg>
  )},
  { to: '/dashboard', label: 'SLA Dashboard', permission: 'view_dashboard', icon: (
    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" /></svg>
  )},
  { to: '/settings', label: 'Settings', permission: 'manage_users', icon: (
    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" /><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" /></svg>
  )},
];

// My HR sub-nav items — shown indented when any /my-hr/* route is active
const hrNavItems = [
  { to: '/my-hr',              label: 'Check In / Out' },
  { to: '/my-hr/attendance',   label: 'Attendance' },
  { to: '/my-hr/history',      label: 'History' },
  { to: '/my-hr/leaves',       label: 'Leaves' },
  { to: '/my-hr/direct-call',  label: 'Direct Call' },
  { to: '/my-hr/team',         label: 'Team' },
  { to: '/my-hr/profile',      label: 'Profile' },
];

function Avatar({ name, size = 'md' }) {
  const initials = (name || 'U').split(' ').map(w => w[0]).join('').toUpperCase().slice(0, 2);
  const sz = size === 'sm' ? 'w-8 h-8 text-xs' : 'w-10 h-10 text-sm';
  return (
    <div className={`${sz} rounded-full bg-magenta-500 flex items-center justify-center text-white font-bold flex-shrink-0`}>
      {initials}
    </div>
  );
}

export default function AppLayout() {
  const dispatch    = useDispatch();
  const navigate    = useNavigate();
  const location    = useLocation();
  const user        = useSelector(s => s.auth.user) || JSON.parse(localStorage.getItem('user') || '{}');
  const myStatus    = useSelector(s => s.availability.myStatus);
  const permMatrix  = useSelector(s => s.permissions.matrix);

  const isHRActive  = location.pathname.startsWith('/my-hr');
  const [hrOpen, setHrOpen] = useState(isHRActive);

  // Keep HR section open whenever we're on an HR route
  useEffect(() => { if (isHRActive) setHrOpen(true); }, [isHRActive]);

  const handleLogout = () => { dispatch(logout()); navigate('/login'); };

  const [pwModalOpen, setPwModalOpen] = useState(false);
  const [pwCurrent, setPwCurrent] = useState('');
  const [pwNew, setPwNew] = useState('');
  const [pwConfirm, setPwConfirm] = useState('');
  const [pwError, setPwError] = useState('');
  const [pwSuccess, setPwSuccess] = useState(false);
  const [pwSaving, setPwSaving] = useState(false);

  const openPwModal = () => {
    setPwCurrent(''); setPwNew(''); setPwConfirm('');
    setPwError(''); setPwSuccess(false);
    setPwModalOpen(true);
  };

  const submitPasswordChange = async () => {
    setPwError('');
    if (!pwCurrent || !pwNew || !pwConfirm) { setPwError('All fields are required.'); return; }
    if (pwNew.length < 6) { setPwError('New password must be at least 6 characters.'); return; }
    if (pwNew !== pwConfirm) { setPwError('New password and confirmation do not match.'); return; }
    setPwSaving(true);
    try {
      await client.post('/auth/change-password/', { current_password: pwCurrent, new_password: pwNew });
      setPwSuccess(true);
      setTimeout(() => setPwModalOpen(false), 1200);
    } catch (err) {
      setPwError(err?.response?.data?.error || 'Failed to change password.');
    } finally {
      setPwSaving(false);
    }
  };

  const can = (key) => {
    if (!user?.role) return false;
    if (user.role === 'ADMIN') return true;
    if (!permMatrix[user.role]) return true;
    return permMatrix[user.role][key] === true;
  };

  const visibleNav = mainNavItems.filter(item => {
    if (item.to === '/tl-dashboard' && !['ADMIN', 'TEAM_LEAD'].includes(user.role)) return false;
    if (!item.permission || can(item.permission)) {
      // Hardcoded restriction: SLA Dashboard is not for Resident Engineers.
      if (item.to === '/dashboard' && user.role === 'RE') return false;
      return true;
    }
    return false;
  });

  useEffect(() => {
    dispatch(fetchAvailability());
    dispatch(fetchRolePermissions());
    client.get('/auth/availability/me/')
      .then(({ data }) => dispatch(setMine(data)))
      .catch(() => {});
  }, [dispatch]);

  const navLinkClass = (isActive) =>
    `group flex items-center gap-3 px-3 py-2.5 rounded-xl text-sm font-medium transition-all duration-150 ${
      isActive
        ? 'bg-magenta-500 text-white shadow-lg shadow-magenta-900/30'
        : 'text-purple-200 hover:bg-white/10 hover:text-white'
    }`;

  const iconClass = (isActive) =>
    isActive ? 'text-white' : 'text-purple-300 group-hover:text-white transition-colors';

  return (
    <div className="flex h-screen overflow-hidden bg-brand-50">
      {/* Sidebar */}
      <aside className="w-64 flex-shrink-0 flex flex-col" style={{background: 'linear-gradient(160deg, #3D3562 0%, #272244 100%)'}}>

        {/* Logo */}
        <div className="px-6 py-6 border-b border-white/10">
          <div className="flex items-center gap-3">
            <div className="w-9 h-9 flex items-center justify-center flex-shrink-0">
              <svg viewBox="0 0 40 44" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M20 2L38 40H2L20 2Z" fill="#C1278D"/>
                <circle cx="20" cy="17" r="4" fill="white"/>
                <rect x="17" y="20" width="6" height="10" rx="1" fill="white"/>
              </svg>
            </div>
            <div>
              <p className="text-white font-bold text-base leading-none tracking-tight">PROACTIVE</p>
              <p className="text-purple-300 text-xs mt-0.5 font-medium">BHEL Ticketing</p>
            </div>
          </div>
        </div>

        {/* Nav */}
        <nav className="flex-1 px-3 py-4 space-y-0.5 overflow-y-auto">

          {/* Main nav items */}
          {visibleNav.map(item => (
            <NavLink key={item.to} to={item.to}
              className={({ isActive }) => navLinkClass(isActive)}>
              {({ isActive }) => (
                <>
                  <span className={iconClass(isActive)}>{item.icon}</span>
                  {item.label}
                </>
              )}
            </NavLink>
          ))}

          {/* ── My HR section ── */}
          <div className="pt-1">
            {/* My HR parent button */}
            <button
              onClick={() => { setHrOpen(o => !o); if (!isHRActive) navigate('/my-hr'); }}
              className={`w-full group flex items-center gap-3 px-3 py-2.5 rounded-xl text-sm font-medium transition-all duration-150 ${
                isHRActive
                  ? 'bg-magenta-500 text-white shadow-lg shadow-magenta-900/30'
                  : 'text-purple-200 hover:bg-white/10 hover:text-white'
              }`}
            >
              <span className={iconClass(isHRActive)}>
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                    d="M5.121 17.804A13.937 13.937 0 0112 16c2.5 0 4.847.655 6.879 1.804M15 10a3 3 0 11-6 0 3 3 0 016 0zm6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
              </span>
              <span className="flex-1 text-left">My HR</span>
              <svg className={`w-4 h-4 transition-transform duration-200 ${hrOpen ? 'rotate-90' : ''} ${isHRActive ? 'text-white' : 'text-purple-400'}`}
                fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
              </svg>
            </button>

            {/* HR sub-items */}
            {hrOpen && (
              <div className="ml-4 mt-0.5 space-y-0.5 border-l border-white/10 pl-3">
                {hrNavItems.map(item => (
                  <NavLink key={item.to} to={item.to} end={item.to === '/my-hr'}
                    className={({ isActive }) =>
                      `flex items-center px-3 py-2 rounded-lg text-sm transition-all duration-150 ${
                        isActive
                          ? 'bg-white/15 text-white font-medium'
                          : 'text-purple-300 hover:bg-white/10 hover:text-white'
                      }`
                    }
                  >
                    {item.label}
                  </NavLink>
                ))}

                {/* HR Admin — only for BHEL ADMIN */}
                {user?.role === 'ADMIN' && (
                  <NavLink to="/my-hr/admin"
                    className={({ isActive }) =>
                      `flex items-center px-3 py-2 rounded-lg text-sm transition-all duration-150 ${
                        isActive
                          ? 'bg-white/15 text-white font-medium'
                          : 'text-purple-300 hover:bg-white/10 hover:text-white'
                      }`
                    }
                  >
                    HR Admin
                  </NavLink>
                )}
              </div>
            )}
          </div>

          {/* BHEL Admin section */}
          {user?.role === 'ADMIN' && (() => {
            const adminActive = location.pathname.startsWith('/admin');
            return (
              <div className="mt-1">
                <Link to="/admin/locations" className={navLinkClass(adminActive)}>
                  <span className={iconClass(adminActive)}>
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                        d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" />
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                    </svg>
                  </span>
                  Admin
                </Link>
              </div>
            );
          })()}
        </nav>

        {/* Quick action */}
        <div className="px-3 pb-3">
          <button onClick={() => navigate('/tickets/new')}
            className="w-full flex items-center justify-center gap-2 bg-magenta-500 hover:bg-magenta-400 text-white text-sm font-semibold py-2.5 rounded-xl transition-all duration-150 shadow-lg shadow-magenta-900/20">
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M12 4v16m8-8H4" />
            </svg>
            New Ticket
          </button>
        </div>

        {/* User */}
        <div className="px-4 py-4 border-t border-white/10">
          {myStatus !== null && (user.role === 'RE' || user.role === 'ADMIN') && (
            <div className="mb-3 flex justify-center">
              <StatusPill status={myStatus}
                onToggle={() => dispatch(setMyStatus(CYCLE[myStatus] || 'ONLINE'))} />
            </div>
          )}
          <div className="flex items-center gap-3">
            <Avatar name={user.full_name || user.email} />
            <div className="flex-1 min-w-0">
              <p className="text-white text-sm font-medium truncate">{user.full_name || 'Admin'}</p>
              <p className="text-purple-300 text-xs truncate">{(user.role || '').replace('_', ' ')}</p>
            </div>
            <button onClick={openPwModal} title="Change password"
              className="text-purple-300 hover:text-white transition-colors flex-shrink-0">
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                  d="M15 7a2 2 0 012 2m4 0a6 6 0 01-7.743 5.743L11 17H9v2H7v2H4a1 1 0 01-1-1v-2.586a1 1 0 01.293-.707l5.964-5.964A6 6 0 1121 9z" />
              </svg>
            </button>
            <button onClick={handleLogout} title="Sign out"
              className="text-purple-300 hover:text-white transition-colors flex-shrink-0">
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                  d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1" />
              </svg>
            </button>
          </div>
        </div>
      </aside>

      {/* Change Password Modal — available to every logged-in user regardless of role */}
      {pwModalOpen && (
        <div className="fixed inset-0 bg-brand-900/60 backdrop-blur-sm flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-sm border border-brand-100">
            <div className="px-6 py-5 border-b border-brand-50">
              <h3 className="text-base font-bold text-brand-500">Change Password</h3>
              <p className="text-gray-400 text-xs mt-0.5">Update the password you use to sign in.</p>
            </div>
            <div className="px-6 py-5 space-y-3">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1.5">Current Password</label>
                <input type="password" value={pwCurrent} onChange={e => setPwCurrent(e.target.value)}
                  className="input-field text-sm" autoFocus />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1.5">New Password</label>
                <input type="password" value={pwNew} onChange={e => setPwNew(e.target.value)}
                  className="input-field text-sm" placeholder="Min 6 characters" />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1.5">Confirm New Password</label>
                <input type="password" value={pwConfirm} onChange={e => setPwConfirm(e.target.value)}
                  className="input-field text-sm" />
              </div>
              {pwError && <p className="text-xs text-red-500 font-medium">{pwError}</p>}
              {pwSuccess && <p className="text-xs text-emerald-600 font-medium">Password updated ✓</p>}
            </div>
            <div className="px-6 py-4 border-t border-brand-50 flex gap-2 justify-end">
              <button onClick={() => setPwModalOpen(false)}
                className="px-4 py-2 border border-gray-200 rounded-xl text-sm font-semibold text-gray-500 hover:bg-gray-50">
                Cancel
              </button>
              <button onClick={submitPasswordChange} disabled={pwSaving}
                className="px-4 py-2 bg-brand-500 text-white rounded-xl text-sm font-semibold hover:bg-brand-400 disabled:opacity-50">
                {pwSaving ? 'Saving…' : 'Update Password'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Main */}
      <main className="flex-1 overflow-auto bg-[#F5F3FC]">
        <Outlet />
      </main>
    </div>
  );
}
